import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

interface WorkReport {
  id: string;
  user_id: string;
  site_name: string;
  work_type: string;
  work_description: string;
  worker_count: number;
  work_hours: number;
  region: string;
  weather: string;
  work_date: string;
  remarks: string;
  created_at: string;
  updated_at: string;
  profile?: { name: string; role: string } | null;
}

const workTypes = ["전체", "칩핑", "파쇄", "상차", "정리", "정비", "기타"];
const regions = ["전체", "서울", "경기", "인천", "부산", "대구", "광주", "대전"];

const workTypeColors: Record<string, string> = {
  "칩핑": "bg-amber-100 text-amber-700",
  "파쇄": "bg-orange-100 text-orange-700",
  "상차": "bg-teal-100 text-teal-700",
  "정리": "bg-green-100 text-green-700"
};

export default function ReportListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reports, setReports] = useState<WorkReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const [filters, setFilters] = useState({
    workType: "전체",
    region: "전체",
    dateFrom: "",
    dateTo: "",
    search: ""
  });

  useEffect(() => {
    async function fetchReports() {
      setLoading(true);
      setFetchError("");
      try {
        const reportData = await api.get<WorkReport[]>("/reports");
        setReports(reportData);
      } catch {
        setFetchError("일보 데이터를 불러오는 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    }

    fetchReports();
  }, []);

  const filteredReports = useMemo(() => {
    return reports.filter((report) => {
      if (filters.workType !== "전체" && report.work_type !== filters.workType) return false;
      if (filters.region !== "전체" && !report.region.startsWith(filters.region)) return false;
      if (filters.dateFrom && report.work_date < filters.dateFrom) return false;
      if (filters.dateTo && report.work_date > filters.dateTo) return false;
      if (filters.search) {
        const s = filters.search.toLowerCase();
        const profileName = report.profile?.name || "";
        return (
          report.site_name.toLowerCase().includes(s) ||
          report.work_description.toLowerCase().includes(s) ||
          profileName.toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [reports, filters]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-400">일보 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-red-400 text-2xl"></i>
        </div>
        <p className="text-sm text-gray-600 mb-3">{fetchError}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">작업일보 목록</h1>
          <p className="text-sm text-gray-400 mt-0.5">총 {filteredReports.length}건의 작업일보</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/report/new")}
            className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            일보 작성
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl p-5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              placeholder="현장명, 작업내용, 작성자 검색..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg p-1">
              {workTypes.slice(0, 8).map(type => (
                <button
                  key={type}
                  onClick={() => setFilters(prev => ({ ...prev, workType: type }))}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    filters.workType === type
                      ? "bg-white text-gray-900"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>

            <div className="relative">
              <select
                value={filters.region}
                onChange={(e) => setFilters(prev => ({ ...prev, region: e.target.value }))}
                className="pl-3 pr-8 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 cursor-pointer"
              >
                {regions.map(r => (
                  <option key={r} value={r}>{r === "전체" ? "전체 지역" : r}</option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
            </div>

            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              />
              <span className="text-gray-300 text-sm">~</span>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              />
            </div>

            {(filters.workType !== "전체" || filters.region !== "전체" || filters.dateFrom || filters.dateTo || filters.search) && (
              <button
                onClick={() => setFilters({ workType: "전체", region: "전체", dateFrom: "", dateTo: "", search: "" })}
                className="px-3 py-2 text-sm text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line mr-1"></i>초기화
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">작업일자</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">현장명</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">작업 유형</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">작업 내용</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">인원</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">시간</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">작성자</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">지역</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider w-20">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredReports.map((report) => (
                <tr
                  key={report.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/report/${report.id}`)}
                >
                  <td className="px-5 py-3.5">
                    <span className="text-sm text-gray-900 whitespace-nowrap">{report.work_date}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-sm font-medium text-gray-900 truncate block max-w-[180px]">{report.site_name}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${workTypeColors[report.work_type] || "bg-gray-100 text-gray-600"}`}>
                      {report.work_type}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-sm text-gray-600 truncate max-w-[250px]">{report.work_description}</p>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-sm text-gray-900">{report.worker_count}명</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-sm text-gray-900">{report.work_hours}h</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-sm text-gray-600 whitespace-nowrap">{report.profile?.name || "알 수 없음"}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-sm text-gray-500">{report.region}</span>
                  </td>
                  <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                    {user?.id === report.user_id && (
                      <button
                        onClick={() => navigate(`/report/${report.id}/edit`)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 cursor-pointer transition-colors"
                        title="수정"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredReports.length === 0 && (
          <div className="py-16 text-center">
            <div className="w-14 h-14 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-file-search-line text-gray-300 text-xl"></i>
            </div>
            <p className="text-gray-400 text-sm">검색 결과가 없습니다</p>
          </div>
        )}
      </div>
    </div>
  );
}